import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/frontend-plugin-api';
import { ButtonIcon, Tooltip, TooltipTrigger } from '@backstage/ui';
import { RiSparkling2Fill } from '@remixicon/react';
import useAsync from 'react-use/lib/useAsync';
import { useChatDrawer } from '../../drawerApi';
import { ChatSkeleton } from './ChatSkeleton';
import styles from './ChatDrawer.module.css';

const Chat = lazy(() => import('../Chat').then(m => ({ default: m.Chat })));

/**
 * The right-hand chat panel and the floating button that opens it, rendered at
 * the app root on every page — the launcher sits on the side the panel opens.
 *
 * Mounted on first open and only hidden after that, so closing it neither
 * drops the conversation nor stops a stream.
 */
export const ChatDrawer = () => {
  const identityApi = useApi(identityApiRef);
  const { open, expanded, api } = useChatDrawer();
  const drawerRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<Element | null>(null);
  const [mounted, setMounted] = useState(open);
  if (open && !mounted) setMounted(true);

  // Root elements also render beside the sign-in page, and the identity proxy
  // resolves only once someone has signed in.
  const { value: signedIn } = useAsync(async () => {
    await identityApi.getBackstageIdentity();
    return true;
  }, [identityApi]);

  useEffect(() => {
    if (!signedIn) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'j') {
        event.preventDefault();
        api.toggle();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [api, signedIn]);

  // Hand focus back to whatever opened the drawer, unless the reader has
  // already moved it somewhere else on the page.
  useEffect(() => {
    if (open) {
      const active = document.activeElement;
      if (!drawerRef.current?.contains(active)) returnFocusRef.current = active;
      return;
    }
    const target = returnFocusRef.current;
    const active = document.activeElement;
    if (
      target instanceof HTMLElement &&
      (!active || active === document.body)
    ) {
      target.focus();
    }
    returnFocusRef.current = null;
  }, [open]);

  if (!signedIn) return null;

  let mode = 'closed';
  if (open) mode = expanded ? 'expanded' : 'open';

  return (
    <>
      {/* Hidden rather than unmounted, so closing can hand focus back to it. */}
      <div className={styles.launcher} hidden={open}>
        <TooltipTrigger>
          <ButtonIcon
            variant="primary"
            icon={<RiSparkling2Fill />}
            aria-label="Ask AI"
            aria-keyshortcuts="Control+J Meta+J"
            onPress={() => api.open()}
          />
          <Tooltip placement="left">Ask AI · Ctrl/⌘ J</Tooltip>
        </TooltipTrigger>
      </div>

      {mounted && (
        <aside
          ref={drawerRef}
          className={styles.drawer}
          aria-label="AI chat"
          hidden={!open}
          // The page-wide push and the width both key on this attribute.
          data-llm-chat-drawer={mode}
        >
          <Suspense fallback={<ChatSkeleton />}>
            <Chat />
          </Suspense>
        </aside>
      )}
    </>
  );
};
